import React, {useEffect} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FaExternalLinkAlt } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { logout } from "store/authSlice";
import ORSProfile from "components/globals/ORSProfile";
import { FaGear, FaImages, FaCarRear, FaUsers, FaBiohazard, FaCirclePlus, FaPencil } from "react-icons/fa6";
import clsx from "clsx";

import {
  PSCollapsableContainer,
  PSCollapsableContainerHeading,
  PSCollapsableContainerContent,
} from "components/globals/ui/PSCollapsableContainer";


function scrollToHash(hash, offset = 100) {
  if (!hash) return;
  const id = hash.replace('#', '');
  const el = document.getElementById(id);
  if (el) {
    const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
}



const SidebarSubContainer = function({children}) {
  return (
    <div className="pt-1 pb-2 px-4">
      {children}
    </div>
  )
}

const SidebarSublink = function({name, to}) {
  return (
    <NavLink className="block mb-3 text-black/70" to={to}>{name}</NavLink>
  )
}

const SidebarGroup = function({pattern, title, children, Icon, green = false}) {
  const location = useLocation();
  return (
    <PSCollapsableContainer startShown={location.pathname.includes(pattern)} noBorders>
      <PSCollapsableContainerHeading>
        <div className="flex items-center">

          <div
            className={clsx(
              green && 'text-green'
            )}
          ><Icon /></div>
          
          <span className="ml-2">{title}</span>
        </div>
      </PSCollapsableContainerHeading>

      <PSCollapsableContainerContent>
        <div>{children}</div>
      </PSCollapsableContainerContent>
    </PSCollapsableContainer>
  )
}

const MobileSidebarNav = function({userInfo}) {

  const dispatch = useDispatch();

  const handleLogout = function () {
    dispatch(logout());
  };

  
  return (
    <div className="sm:hidden flex fixed z-[10] bg-background items-stretch justify-between py-2 w-full px-xs">

      <MobileSidebarNavItem to="/dashboard/overview" pattern="/dashboard/overview" title="My Stuff" Icon={FaImages} />
      <MobileSidebarNavItem to="/dashboard/garage" pattern="/dashboard/garage" title="Garage" Icon={FaCarRear} />
      <MobileSidebarNavItem to="/dashboard/notes" pattern="/dashboard/notes" title="Notes" Icon={FaPencil} />
      <MobileSidebarNavItem to="/dashboard/allfollow" pattern="/dashboard/allfollow" title="Follow" Icon={FaUsers} />
      <MobileSidebarNavItem to="/dashboard/settings" pattern="/dashboard/settings" title="Settings" Icon={FaGear} />

      <div className=" text-guards block text-center py-2 px-2 xs:px-3 rounded-md cursor-pointer " onClick={handleLogout}>
        <div className="flex justify-center">
          <FaExternalLinkAlt />
        </div>

        <span className="font-[700] text-[10px]">Logout</span>
      </div>

    </div>
  )
}

const MobileSidebarNavItem = function({to, pattern, title, Icon, green = false}) {
  const location = useLocation();
  const active = location.pathname.includes(pattern);
  return (
    <NavLink
      to={to} 
      className={clsx(
        'block text-center py-2 px-2 xs:px-3 rounded-md cursor-pointer border-[1px]',
        active ? 'border-border-light' : 'border-background'
      )}
    >
      <div
        className={clsx(
          'flex justify-center',
          green && 'text-green'
        )}
      ><Icon /></div>
      
      <span className="font-[700] text-[10px]">{title}</span>
    </NavLink>
  )
}

export const DashboardSidebar = function({userInfo}) {
  const dispatch = useDispatch();
  const location = useLocation();

  const handlelogout = function () {
    dispatch(logout());
  };

  useEffect(() => {
    scrollToHash(location.hash);
  }, [location]);


  return (

    <>
      <MobileSidebarNav userInfo={userInfo} />
      <div className="
        hidden
        sm:flex
        md:w-[250px]
        shrink-0 
        justify-start
        flex-col
        sticky
        top-[63px] 
        
        text-[13px]
        overflow-hidden
        p-3">

        <div className="mb-xs">

          <SidebarGroup pattern="/dashboard/new" title="Create Content" Icon={FaCirclePlus} green>
            <SidebarSubContainer>
              <SidebarSublink name="Post" to="/new/post" />
              <SidebarSublink name="Garage Car" to="/new/car" />
              <SidebarSublink name="Event" to="/new/event" />
              <SidebarSublink name="Project" to="/new/project" />
              {userInfo?.accountType === "admin" && (
                <div className="text-guards">
                  <SidebarSublink name="Admin: Article" to="/new/article" />
                </div>
              )}
            </SidebarSubContainer>
          </SidebarGroup>
          
          <SidebarGroup pattern="/dashboard/overview" title="My Content" Icon={FaImages}>
            <SidebarSubContainer>
              <SidebarSublink name="Updates" to="/dashboard/overview#updates" />
              <SidebarSublink name="Projects" to="/dashboard/overview#projects" />
              <SidebarSublink name="Events" to="/dashboard/overview#events" />
              <SidebarSublink name="Listings" to="/dashboard/overview#listings" />
              <SidebarSublink name="Want-Ads" to="/dashboard/overview#wants" />
            </SidebarSubContainer>
          </SidebarGroup>

          <SidebarGroup pattern="/dashboard/garage" title="Garage" Icon={FaCarRear}>
            <SidebarSubContainer>
              <SidebarSublink name="Public Cars" to="/dashboard/garage#carsPublic" />
              <SidebarSublink name="Private Cars" to="/dashboard/garage#carsPrivate" />
            </SidebarSubContainer>
          </SidebarGroup>

          <SidebarGroup pattern="/dashboard/notes" title="Notes/Resources" Icon={FaPencil}>
            <SidebarSubContainer>
              <SidebarSublink name="Car Notes" to="/dashboard/notes" />
              <SidebarSublink name="Resources" to="/dashboard/notes#resources" />
            </SidebarSubContainer>
          </SidebarGroup>

          <SidebarGroup pattern="/dashboard/follow" title="Followers" Icon={FaUsers}>
            <SidebarSubContainer>
              <SidebarSublink name="Followers" to="/dashboard/followers" />
              <SidebarSublink name="Following" to="/dashboard/following" />
            </SidebarSubContainer>
          </SidebarGroup>

          <SidebarGroup pattern="/dashboard/settings" title="Settings" Icon={FaGear}>
            <SidebarSubContainer>
              <SidebarSublink name="User Settings" to="/dashboard/settings#usersettings" />
              <SidebarSublink name="Notification Settings" to="/dashboard/settings#notificationsettings" />
            </SidebarSubContainer>
          </SidebarGroup>

          {userInfo?.accountType === "admin" && (
            <div className="text-guards">
              <SidebarGroup pattern="/dashboard/admin" title="Admin" Icon={FaBiohazard}>
                <SidebarSubContainer>
                  <SidebarSublink name="Articles" to="/dashboard/articles" />
                </SidebarSubContainer>
              </SidebarGroup>
            </div>
          )}
        </div>

        <div className="bg-white border-[1px] border-border/light p-3 rounded-md flex items-center">
          <div className="shrink-0 mr-2">
            <ORSProfile />
          </div>
          <div className="grow">
            <NavLink className="cursor-pointer flex items-center shrink-0 mb-1 hover:underline" to={`/users/${userInfo?.username}`}>
              <span className="block text-[12px]  tracking-[.2px]">Public Profile</span>
              <span className="text-[12px] block ml-1">
                <FaExternalLinkAlt />
              </span>
            </NavLink>
            <span className="block text-[12px]  tracking-[.2px]ml-5 text-guards cursor-pointer hover:underline" onClick={handlelogout}>
              Logout
            </span>
          </div>
        </div>
      </div>
    </>

    
  );
}